import { useRef } from "react";

import { useSavBlocking } from "../../SavBlockingContext";

import { isMinimumAgeReached } from "./BirthDateFormInputs";

type MinimumAgeValidationArgs = {
  year: number;
  month: number;
  day: number;
  minimumAge: number;
  setMinimumAgeError: () => void;
  clearMinimumAgeError: () => void;
};

/**
 * Shared minimum-age validation for the inputs and dropdowns variants.
 *
 * When blocking is enabled, underage state is reported via context (keeping the form
 * submittable so Continue can trigger the block) instead of setting a form error.
 * Otherwise a ref guard prevents re-setting an already-applied error, which would loop.
 */
export const useMinimumAgeValidator = () => {
  const { isBlockingEnabled, setIsUnderage } = useSavBlocking();
  const hasMinimumAgeErrorRef = useRef(false);

  return ({ year, month, day, minimumAge, setMinimumAgeError, clearMinimumAgeError }: MinimumAgeValidationArgs) => {
    const clearMinimumAgeErrorIfSet = () => {
      if (hasMinimumAgeErrorRef.current) {
        hasMinimumAgeErrorRef.current = false;
        clearMinimumAgeError();
      }
    };

    const isDateComplete = Boolean(day && month && year);

    if (isBlockingEnabled) {
      clearMinimumAgeErrorIfSet();
      setIsUnderage(isDateComplete && !isMinimumAgeReached({ year, month, day, minimumAge }));
      return;
    }

    const isEligible = isDateComplete && isMinimumAgeReached({ year, month, day, minimumAge });
    if (!isDateComplete || isEligible) {
      clearMinimumAgeErrorIfSet();
      return;
    }

    if (!hasMinimumAgeErrorRef.current) {
      hasMinimumAgeErrorRef.current = true;
      setMinimumAgeError();
    }
  };
};
